import { Bot, Settings, Layers, Command, Shield, MessageSquare, ClipboardList, Image, Terminal, BarChart3, FileText, Activity, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useMe } from '@/discord/hooks/useMe';
import styles from './DiscordHome.module.scss';

interface SectionCard {
  icon: typeof Bot;
  title: string;
  desc: string;
  to?: string; // route si la page est déjà portée (sinon "à venir")
}

const BOT_SECTIONS: SectionCard[] = [
  { icon: Settings, title: 'Réglages', desc: 'Tickets : ouverture, fermeture, durées, options.' },
  { icon: Layers, title: 'Catégories & slots', desc: 'Catégories de tickets et créneaux disponibles.' },
  { icon: Command, title: 'Commandes', desc: 'Commandes slash du bot et leurs réponses.' },
  { icon: Shield, title: 'Rôles staff', desc: "Rôles Discord autorisés et niveaux d'accès." },
  { icon: MessageSquare, title: 'Messages & Arty', desc: 'Messages génériques, modals et webhooks d’Arty.' },
  { icon: ClipboardList, title: 'Récapitulatif', desc: 'Réglages du récapitulatif de ticket.' },
  { icon: Image, title: 'Interface & images', desc: 'Éléments d’UI et bannières (assets).' },
];

const OPS_SECTIONS: SectionCard[] = [
  { icon: Terminal, title: 'Console', desc: 'Logs en direct du bot et redémarrage.' },
  { icon: BarChart3, title: 'Statistiques', desc: 'Tickets, activité, indicateurs.' },
  { icon: FileText, title: 'Transcripts', desc: 'Transcriptions des tickets clôturés.' },
  { icon: Activity, title: 'Statut', desc: 'État du bot et des services.', to: '/config/discord/exploitation/status' },
];

const VARIANTS = {
  bot: {
    icon: Bot,
    eyebrow: 'Configuration · Discord · Bot',
    title: 'Configuration du bot',
    subtitle: 'Réglages du bot de tickets Discord de Paladium',
    sections: BOT_SECTIONS,
  },
  ops: {
    icon: Activity,
    eyebrow: 'Configuration · Discord · Exploitation',
    title: 'Exploitation du bot',
    subtitle: 'Suivi, statistiques et console du bot de tickets',
    sections: OPS_SECTIONS,
  },
} as const;

export function DiscordHome({ variant }: { variant: 'bot' | 'ops' }) {
  const { user } = useMe();
  const v = VARIANTS[variant];
  const Icon = v.icon;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <span className={styles.brand}><Icon size={22} strokeWidth={2} /></span>
        <div>
          <span className={styles.eyebrow}>{v.eyebrow}</span>
          <h1 className={styles.title}>{v.title}</h1>
          <p className={styles.subtitle}>
            {v.subtitle}
            {user ? <> · connecté en tant que <strong>{user.username}</strong></> : null}.
          </p>
        </div>
      </header>

      <div className={styles.notice}>
        Intégration en cours : l'interface est en place, le branchement sur l'API du bot
        (via notre backend, en gardant votre authentification actuelle) arrive ensuite.
      </div>

      <div className={styles.grid}>
        {v.sections.map((s) => {
          const CardIcon = s.icon;
          const inner = (
            <>
              <span className={styles.cardIcon}><CardIcon size={20} strokeWidth={2} /></span>
              <div className={styles.cardBody}>
                <h3 className={styles.cardTitle}>{s.title}</h3>
                <p className={styles.cardDesc}>{s.desc}</p>
              </div>
              {s.to
                ? <span className={styles.open}><ChevronRight size={18} /></span>
                : <span className={styles.soon}>à venir</span>}
            </>
          );
          return s.to ? (
            <Link key={s.title} to={s.to} className={`${styles.card} ${styles.cardLink}`}>{inner}</Link>
          ) : (
            <article key={s.title} className={styles.card}>{inner}</article>
          );
        })}
      </div>
    </div>
  );
}
